import React, { useEffect, useRef, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import axios from "axios";
import { Environment as environment } from "../../environment.ts";
import { useFlowStore } from "../../store/FlowStateStore.ts";
import VariablePicker from "../Common/VariablePicker.tsx";

interface SheetFile { id: string; name: string; }
interface WriteMapping { column: string; value: string; }
interface ReadCapture { column: string; attributeId: string; }
interface Attribute { attributeId: string; attributeName: string; attributeType?: string; }

interface Props {
    data: any;
    branches: { id: string; label: string }[];
    onClose: (saved: { operation: string; sheetName: string } | null) => void;
}

const GoogleSheetsDialog: React.FC<Props> = ({ data, branches, onClose }) => {
    const { nodes, setNodes, businessId } = useFlowStore();

    const [operation, setOperation]               = useState("APPEND");
    const [spreadsheetId, setSpreadsheetId]       = useState("");
    const [spreadsheetName, setSpreadsheetName]   = useState("");
    const [tabs, setTabs]                         = useState<string[]>([]);
    const [sheetName, setSheetName]               = useState("");
    const [lookupColumn, setLookupColumn]         = useState("");
    const [lookupValue, setLookupValue]           = useState("");
    const [writeMapping, setWriteMapping]         = useState<WriteMapping[]>([]);
    const [readCapture, setReadCapture]           = useState<ReadCapture[]>([]);
    const [attributes, setAttributes]             = useState<Attribute[]>([]);
    const [headers, setHeaders]                   = useState<string[]>([]);
    const [loadingTabs, setLoadingTabs]           = useState(false);
    const [loadingHeaders, setLoadingHeaders]     = useState(false);
    const [pickerLoading, setPickerLoading]       = useState(false);
    // READ_MULTIPLE fields
    const [useRowRange, setUseRowRange]           = useState(false);
    const [rowStart, setRowStart]                 = useState(1);
    const [rowEnd, setRowEnd]                     = useState(10);
    const [filterColumn, setFilterColumn]         = useState("");
    const [filterValue, setFilterValue]           = useState("");
    const [arrayAttributeName, setArrayAttributeName] = useState("");
    const isInitialSpreadsheetLoad = useRef(true);

    // Load saved data
    useEffect(() => {
        const node = nodes.find((n) => n.data.id === data.nodeId);
        const content = (node?.data as any)?.content?.find((c: any) => c.id === data.subNodeId);
        const saved = content?.data as any;
        if (saved) {
            setOperation(saved.operation || "APPEND");
            setSpreadsheetId(saved.spreadsheetId || "");
            setSpreadsheetName(saved.spreadsheetName || "");
            setSheetName(saved.sheetName || "");
            setLookupColumn(saved.lookupColumn || "");
            setLookupValue(saved.lookupValue || "");
            setWriteMapping(saved.writeMapping || []);
            setReadCapture(saved.readCapture || []);
            setUseRowRange(saved.useRowRange ?? false);
            setRowStart(saved.rowStart ?? 1);
            setRowEnd(saved.rowEnd ?? 10);
            setFilterColumn(saved.filterColumn || "");
            setFilterValue(saved.filterValue || "");
            setArrayAttributeName(saved.arrayAttributeName || "");
        }
    }, []);

    // When the flow builder runs inside an iframe (embedded in the dashboard),
    // Google Picker validates against the top-level window origin, not the iframe's own origin.
    const getPickerOrigin = (): string => {
        if (window.self !== window.top && document.referrer) {
            return new URL(document.referrer).origin;
        }
        return window.location.origin;
    };

    const openPicker = () => {
        setPickerLoading(true);
        axios.get(`${environment.baseUrl}/google/access-token?businessId=${businessId}`)
            .then((res) => {
                const accessToken = res.data.accessToken;
                const launchPicker = () => {
                    (window as any).gapi.load('picker', () => {
                        // Inject CSS before picker opens so it renders above the modal overlay immediately
                        const STYLE_ID = 'gpicker-zindex-fix';
                        if (!document.getElementById(STYLE_ID)) {
                            const s = document.createElement('style');
                            s.id = STYLE_ID;
                            s.textContent = '.picker-dialog-bg{z-index:2147483646!important}.picker-dialog{z-index:2147483647!important}';
                            document.head.appendChild(s);
                        }
                        const picker = new (window as any).google.picker.PickerBuilder()
                            .addView(
                                new (window as any).google.picker.DocsView()
                                    .setMimeTypes('application/vnd.google-apps.spreadsheet')
                            )
                            .setOAuthToken(accessToken)
                            .setOrigin(getPickerOrigin())
                            .setCallback((data: any) => {
                                if (data.action === 'picked' || data.action === 'cancel') {
                                    document.getElementById(STYLE_ID)?.remove();
                                }
                                if (data.action === 'picked') {
                                    const file = data.docs[0];
                                    setSpreadsheetId(file.id);
                                    setSpreadsheetName(file.name);
                                    setSheetName('');
                                }
                            })
                            .build();
                        picker.setVisible(true);
                        setPickerLoading(false);
                    });
                };
                if ((window as any).google?.picker) {
                    launchPicker();
                } else {
                    const script = document.createElement('script');
                    script.src = 'https://apis.google.com/js/api.js';
                    script.onload = launchPicker;
                    document.body.appendChild(script);
                }
            })
            .catch(() => setPickerLoading(false));
    };

    // Load tabs when spreadsheet changes
    useEffect(() => {
        if (!spreadsheetId) return;
        setLoadingTabs(true);
        setHeaders([]);
        if (isInitialSpreadsheetLoad.current) {
            isInitialSpreadsheetLoad.current = false;  // preserve saved sheetName on initial load
        } else {
            setSheetName("");  // user changed the spreadsheet — reset the tab
        }
        axios.get(`${environment.baseUrl}/google/sheets/${spreadsheetId}/tabs?businessId=${businessId}`)
            .then((res) => setTabs(res.data))
            .catch(() => {})
            .finally(() => setLoadingTabs(false));
    }, [spreadsheetId]);

    // Load headers when sheet tab is selected
    useEffect(() => {
        if (!spreadsheetId || !sheetName) { setHeaders([]); return; }
        setLoadingHeaders(true);
        axios.get(`${environment.baseUrl}/google/sheets/${spreadsheetId}/columns?businessId=${businessId}&sheetName=${encodeURIComponent(sheetName)}`)
            .then((res) => setHeaders(res.data || []))
            .catch(() => setHeaders([]))
            .finally(() => setLoadingHeaders(false));
    }, [spreadsheetId, sheetName]);

    // Load attributes
    useEffect(() => {
        axios.put(`${environment.baseUrl}/fetch/attribute`, { businessId, page: 0, size: 100 })
            .then((res) => setAttributes(res.data.data.data))
            .catch(() => {});
    }, []);

    const handleSave = () => {
        const updatedNodes = nodes.map((node) => {
            if (node.data.id !== data.nodeId) return node;
            return {
                ...node,
                data: {
                    ...node.data,
                    content: (node.data as any).content.map((c: any) => {
                        if (c.id !== data.subNodeId) return c;
                        return {
                            ...c,
                            data: {
                                        operation,
                                spreadsheetId,
                                spreadsheetName,
                                sheetName,
                                lookupColumn,
                                lookupValue,
                                writeMapping,
                                readCapture,
                                useRowRange,
                                rowStart: useRowRange ? rowStart : 1,
                                rowEnd:   useRowRange ? rowEnd   : 20,
                                filterColumn,
                                filterValue,
                                arrayAttributeName,
                                successNode: branches[0].id,
                                failureNode: branches[1].id,
                            }
                        };
                    })
                }
            };
        });
        setNodes(updatedNodes as any);
        onClose({ operation, sheetName });
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <div style={{ flex: 1, overflowY: "auto", padding: "24px", maxHeight: "65vh" }}>

                {/* Operation */}
                <div style={{ marginBottom: "16px" }}>
                    <label style={labelStyle}>Operation</label>
                    <select value={operation} onChange={(e) => setOperation(e.target.value)} style={inputStyle}>
                        <option value="APPEND">Append Row</option>
                        <option value="UPDATE">Update Row</option>
                        <option value="READ">Read Row</option>
                        <option value="READ_MULTIPLE">Read Multiple Rows</option>
                    </select>
                </div>

                {/* Spreadsheet — Google Picker */}
                <div style={{ marginBottom: "16px" }}>
                    <label style={labelStyle}>Spreadsheet</label>
                    {spreadsheetName ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ flex: 1, fontSize: "13px", color: "#1e293b", padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: "6px", background: "#f8fafc" }}>{spreadsheetName}</span>
                            <button onClick={openPicker} style={outlineButtonStyle} disabled={pickerLoading}>
                                {pickerLoading ? "Opening..." : "Change"}
                            </button>
                        </div>
                    ) : (
                        <button onClick={openPicker} style={{ ...outlineButtonStyle, width: "100%", justifyContent: "center" }} disabled={pickerLoading}>
                            {pickerLoading ? "Opening..." : "Choose from Google Drive"}
                        </button>
                    )}
                </div>

                {/* Tab */}
                {spreadsheetId && (
                    <div style={{ marginBottom: "16px" }}>
                        <label style={labelStyle}>Sheet / Tab</label>
                        <select value={sheetName} onChange={(e) => setSheetName(e.target.value)} style={inputStyle} disabled={loadingTabs}>
                            <option value="">{loadingTabs ? "Loading..." : "-- Select tab --"}</option>
                            {tabs.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                )}

                {/* Lookup — UPDATE and READ */}
                {sheetName && (operation === "UPDATE" || operation === "READ") && (
                    <div style={{ padding: "16px", background: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0", marginBottom: "16px" }}>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "12px" }}>Lookup</div>
                        <div style={{ display: "flex", gap: "12px" }}>
                            <div style={{ flex: "0 0 160px" }}>
                                <label style={labelStyle}>Column</label>
                                <select value={lookupColumn} onChange={(e) => setLookupColumn(e.target.value)} style={inputStyle} disabled={loadingHeaders}>
                                    <option value="">{loadingHeaders ? "Loading..." : "-- Select column --"}</option>
                                    {headers.map(h => <option key={h} value={h}>{h}</option>)}
                                </select>
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={labelStyle}>Value</label>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <input
                                        type="text"
                                        placeholder="e.g. {{order_id}}"
                                        value={lookupValue}
                                        onChange={(e) => setLookupValue(e.target.value)}
                                        style={{ ...inputStyle, flex: 1 }}
                                    />
                                    <VariablePicker onSelect={(val) => setLookupValue((prev) => prev + `{{${val}}}`)} />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Write Mapping — APPEND and UPDATE */}
                {sheetName && (operation === "APPEND" || operation === "UPDATE") && (
                    <div style={{ marginBottom: "16px" }}>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "12px" }}>Column Mapping</div>
                        {writeMapping.map((m, i) => (
                            <div key={i} style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "10px" }}>
                                <div style={{ flex: "0 0 140px" }}>
                                    {i === 0 && <label style={labelStyle}>Column</label>}
                                    <select value={m.column}
                                        onChange={(e) => { const u = [...writeMapping]; u[i] = { ...u[i], column: e.target.value }; setWriteMapping(u); }}
                                        style={inputStyle} disabled={loadingHeaders}>
                                        <option value="">{loadingHeaders ? "Loading..." : "-- Column --"}</option>
                                        {headers.map(h => <option key={h} value={h}>{h}</option>)}
                                    </select>
                                </div>
                                <div style={{ flex: 1 }}>
                                    {i === 0 && <label style={labelStyle}>Value / Attribute</label>}
                                    <input
                                        type="text"
                                        placeholder="{{attribute}} or text"
                                        value={m.value}
                                        onChange={(e) => {
                                            const u = [...writeMapping];
                                            u[i] = { ...u[i], value: e.target.value };
                                            setWriteMapping(u);
                                        }}
                                        style={inputStyle}
                                    />
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: i === 0 ? "18px" : "0" }}>
                                    <button
                                        onClick={() => setWriteMapping(writeMapping.filter((_, idx) => idx !== i))}
                                        style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: "4px" }}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                    <VariablePicker onSelect={(val) => {
                                        const u = [...writeMapping];
                                        u[i] = { ...u[i], value: (u[i].value || "") + `{{${val}}}` };
                                        setWriteMapping(u);
                                    }} />
                                </div>
                            </div>
                        ))}
                        <button
                            onClick={() => setWriteMapping([...writeMapping, { column: "", value: "" }])}
                            style={outlineButtonStyle}
                        >
                            <Plus size={14} /> Add Column
                        </button>
                    </div>
                )}

                {/* Read Capture — READ */}
                {sheetName && operation === "READ" && (
                    <div style={{ marginBottom: "16px" }}>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "12px" }}>Capture Into Attributes</div>
                        {readCapture.map((cap, i) => (
                            <div key={i} style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "10px" }}>
                                <div style={{ flex: "0 0 140px" }}>
                                    {i === 0 && <label style={labelStyle}>Column</label>}
                                    <select value={cap.column}
                                        onChange={(e) => { const u = [...readCapture]; u[i] = { ...u[i], column: e.target.value }; setReadCapture(u); }}
                                        style={inputStyle} disabled={loadingHeaders}>
                                        <option value="">{loadingHeaders ? "Loading..." : "-- Column --"}</option>
                                        {headers.map(h => <option key={h} value={h}>{h}</option>)}
                                    </select>
                                </div>
                                <div style={{ flex: 1 }}>
                                    {i === 0 && <label style={labelStyle}>Save to Attribute</label>}
                                    <select
                                        value={cap.attributeId}
                                        onChange={(e) => {
                                            const u = [...readCapture];
                                            u[i] = { ...u[i], attributeId: e.target.value };
                                            setReadCapture(u);
                                        }}
                                        style={inputStyle}
                                    >
                                        <option value="">-- Select attribute --</option>
                                        {attributes.map((a) => (
                                            <option key={a.attributeId} value={a.attributeId}>{a.attributeName}</option>
                                        ))}
                                    </select>
                                </div>
                                <button
                                    onClick={() => setReadCapture(readCapture.filter((_, idx) => idx !== i))}
                                    style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: "4px", marginTop: i === 0 ? "18px" : "0" }}
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        ))}
                        <button
                            onClick={() => setReadCapture([...readCapture, { column: "", attributeId: "" }])}
                            style={outlineButtonStyle}
                        >
                            <Plus size={14} /> Add Capture
                        </button>
                    </div>
                )}
                {/* READ_MULTIPLE */}
                {sheetName && operation === "READ_MULTIPLE" && (
                    <div style={{ marginBottom: "16px" }}>

                        {/* Row range toggle */}
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
                            <input type="checkbox" id="useRowRange" checked={useRowRange}
                                onChange={(e) => setUseRowRange(e.target.checked)}
                                style={{ width: "16px", height: "16px", cursor: "pointer" }} />
                            <label htmlFor="useRowRange" style={{ ...labelStyle, margin: 0, cursor: "pointer" }}>
                                Limit by row range
                            </label>
                        </div>

                        {useRowRange && (
                            <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
                                <div style={{ flex: 1 }}>
                                    <label style={labelStyle}>Start Row</label>
                                    <input type="number" min={1} max={100} value={rowStart}
                                        onChange={(e) => setRowStart(Math.max(1, parseInt(e.target.value) || 1))}
                                        style={inputStyle} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={labelStyle}>End Row <span style={{ fontWeight: 400, color: "#94a3b8" }}>(max 20)</span></label>
                                    <input type="number" min={1} max={100} value={rowEnd}
                                        onChange={(e) => setRowEnd(Math.min(100, Math.max(1, parseInt(e.target.value) || 10)))}
                                        style={inputStyle} />
                                </div>
                            </div>
                        )}

                        {/* Optional filter */}
                        <div style={{ padding: "14px", background: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0", marginBottom: "16px" }}>
                            <div style={{ fontSize: "11px", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "12px" }}>
                                Filter <span style={{ fontWeight: 400, color: "#94a3b8", textTransform: "none", letterSpacing: 0 }}>(optional)</span>
                            </div>
                            <div style={{ display: "flex", gap: "12px" }}>
                                <div style={{ flex: 1 }}>
                                    <label style={labelStyle}>Column Name</label>
                                    <input type="text" placeholder="e.g. Status"
                                        value={filterColumn}
                                        onChange={(e) => setFilterColumn(e.target.value)}
                                        style={inputStyle} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={labelStyle}>Value</label>
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                        <input type="text" placeholder="e.g. Active or {{variable}}"
                                            value={filterValue}
                                            onChange={(e) => setFilterValue(e.target.value)}
                                            style={{ ...inputStyle, flex: 1 }} />
                                        <VariablePicker onSelect={(val) => setFilterValue((prev) => prev + `{{${val}}}`)} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Array attribute name — required */}
                        <div>
                            <label style={labelStyle}>
                                Save result to array attribute <span style={{ color: "#ef4444" }}>*</span>
                            </label>
                            <select
                                value={arrayAttributeName}
                                onChange={(e) => setArrayAttributeName(e.target.value)}
                                style={{ ...inputStyle, width: "100%", borderColor: !arrayAttributeName.trim() ? "#ef4444" : undefined }}
                            >
                                <option value="">Select Array Attribute</option>
                                {attributes
                                    .filter(a => a.attributeType?.toUpperCase() === 'ARRAY')
                                    .map(a => (
                                        <option key={a.attributeId} value={a.attributeName}>{a.attributeName}</option>
                                    ))
                                }
                            </select>
                            {!arrayAttributeName.trim() && (
                                <div style={{ fontSize: "11px", color: "#ef4444", marginTop: "4px" }}>
                                    Required — enter a name to store the rows (e.g. products)
                                </div>
                            )}
                            {arrayAttributeName.trim() && (
                                <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "4px" }}>
                                    Each row is stored as a JSON object keyed by column headers. Use this attribute in Carousel or Option List (dynamic mode).
                                </div>
                            )}
                        </div>
                    </div>
                )}

            </div>

            {/* Footer */}
            <div style={{ padding: "16px 24px", borderTop: "1px solid #f1f5f9", background: "#f8fafc", display: "flex", justifyContent: "flex-end" }}>
                <button
                    onClick={handleSave}
                    disabled={operation === "READ_MULTIPLE" && !arrayAttributeName.trim()}
                    style={{ ...saveButtonStyle, opacity: (operation === "READ_MULTIPLE" && !arrayAttributeName.trim()) ? 0.5 : 1, cursor: (operation === "READ_MULTIPLE" && !arrayAttributeName.trim()) ? "not-allowed" : "pointer" }}>
                    Save Configuration
                </button>
            </div>
        </div>
    );
};

const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "12px",
    fontWeight: 600,
    color: "#475569",
    marginBottom: "6px",
};

const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #e2e8f0",
    borderRadius: "6px",
    fontSize: "13px",
    boxSizing: "border-box",
    outline: "none",
};

const outlineButtonStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    background: "white",
    border: "1px solid #e2e8f0",
    color: "#64748b",
    fontSize: "12px",
    fontWeight: 600,
    padding: "6px 12px",
    borderRadius: "6px",
    cursor: "pointer",
};

const saveButtonStyle: React.CSSProperties = {
    background: "var(--primary-bg-color)",
    color: "white",
    padding: "10px 32px",
    borderRadius: "8px",
    fontWeight: 700,
    fontSize: "14px",
    border: "none",
    cursor: "pointer",
};

export default GoogleSheetsDialog;
